/**
 * MERRACHI — Product Page JS
 * Galerie desktop : slider vertical filtré par couleur (alt text)
 * Galerie mobile  : carousel scroll-snap filtré par couleur
 * Variantes       : swatches → filtre galerie + price/URL
 * Onglets         : nav horizontale
 * Formulaire      : ATC ajax + spinner
 */

(function () {
  'use strict';

  function qs(sel, root)  { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return [...(root || document).querySelectorAll(sel)]; }

  function formatMoney(cents) {
    const fmtEl  = qs('[data-mp-money-format]');
    const fmt    = fmtEl ? fmtEl.dataset.mpMoneyFormat : '{{amount}} €';
    const amount = (cents / 100).toFixed(2);
    return fmt
      .replace(/\{\{amount\}\}/g,                amount)
      .replace(/\{\{amount_no_decimals\}\}/g,    String(Math.round(cents / 100)))
      .replace(/\{\{amount_with_comma_separator\}\}/g, amount.replace('.', ','));
  }

  /* =========================================================================
     Desktop gallery — vertical slider with color filtering
     Matching: slide[data-alt].includes(colorName.toLowerCase())
     Hidden slides get height:0 so translateY(N*100vh) still lands correctly
     ========================================================================= */

  class DesktopGallery {
    constructor(el) {
      this.el            = el;
      this.track         = qs('[data-mp-track]', el);
      this.slides        = qsa('[data-mp-slide]', el);
      this.dotsContainer = qs('[data-mp-dots]', el);
      this.current       = 0;
      this.scrolling     = false;
      this.visible       = [...this.slides]; // subset of slides currently shown

      if (!this.track || !this.slides.length) return;

      // Event delegation on dots container (survives innerHTML rebuilds)
      if (this.dotsContainer) {
        this.dotsContainer.addEventListener('click', (e) => {
          const dot = e.target.closest('[data-mp-dot]');
          if (dot) this._goto(parseInt(dot.dataset.index, 10));
        });
      }

      this._bindWheel();
      this._goto(0, false);
    }

    /* ------------------------------------------------------------------
       filterByColor(colorName)
       Called when a color swatch is selected. Hides non-matching slides
       and rebuilds dot navigation. Falls back to all if no match found.
    ------------------------------------------------------------------ */
    filterByColor(colorName) {
      const needle = colorName.toLowerCase().trim();

      let matched = this.slides.filter(s =>
        !needle || (s.dataset.alt || '').includes(needle)
      );

      // Fallback: if no alt text matches, show everything
      if (!matched.length) matched = [...this.slides];

      // Show/hide slides via data attribute (CSS handles height:0)
      this.slides.forEach(s => {
        s.dataset.mpHidden = matched.includes(s) ? '' : 'true';
      });

      this.visible = matched;
      this.current = 0;
      this._buildDots();
      this._goto(0, false);
    }

    /* Rebuild dot buttons to match the current visible set */
    _buildDots() {
      if (!this.dotsContainer) return;

      if (this.visible.length <= 1) {
        this.dotsContainer.style.display = 'none';
        return;
      }
      this.dotsContainer.style.display = '';
      this.dotsContainer.innerHTML = '';

      this.visible.forEach((_, i) => {
        const btn = document.createElement('button');
        btn.className  = 'mp-gallery__dot' + (i === 0 ? ' is-active' : '');
        btn.type       = 'button';
        btn.dataset.mpDot = '';
        btn.dataset.index = i;
        btn.setAttribute('aria-label', `Image ${i + 1}`);
        this.dotsContainer.appendChild(btn);
      });
    }

    /* Navigate to the Nth VISIBLE slide.
       Because hidden slides have height:0, translateY(N*100vh) is correct. */
    _goto(idx, animate) {
      const len = this.visible.length;
      if (!len) return;
      idx = Math.max(0, Math.min(idx, len - 1));

      this.current   = idx;
      this.scrolling = true;

      this.track.style.transform = `translateY(calc(-${idx} * 100vh))`;

      if (animate === false) {
        this.track.style.transition = 'none';
        requestAnimationFrame(() => { this.track.style.transition = ''; });
      }

      // Update active dot
      qsa('[data-mp-dot]', this.dotsContainer || this.el).forEach((d, i) => {
        d.classList.toggle('is-active', i === idx);
      });

      setTimeout(() => { this.scrolling = false; }, 750);
    }

    _bindWheel() {
      this.el.addEventListener('wheel', (e) => {
        e.preventDefault();
        if (this.scrolling) return;
        if (e.deltaY > 20 && this.current < this.visible.length - 1) {
          this._goto(this.current + 1);
        } else if (e.deltaY < -20 && this.current > 0) {
          this._goto(this.current - 1);
        }
      }, { passive: false });
    }

    // Called by VariantPicker for non-color variant changes (e.g., size)
    setActive(imageId) {
      const id  = String(imageId);
      const idx = this.visible.findIndex(s => s.dataset.imageId === id);
      if (idx > -1) this._goto(idx);
    }
  }

  /* =========================================================================
     Mobile carousel — horizontal scroll-snap with color filtering
     ========================================================================= */

  class MobileCarousel {
    constructor(wrap) {
      this.wrap   = wrap;
      this.track  = qs('.mp-carousel', wrap);
      this.slides = qsa('.mp-carousel-slide', wrap);
      this.dots   = qsa('.mp-dot', wrap);

      if (!this.track || !this.slides.length) return;

      this._bindDots();
      this._observe();
    }

    filterByColor(colorName) {
      const needle = colorName.toLowerCase().trim();

      let matched = this.slides.filter(s =>
        !needle || (s.dataset.alt || '').includes(needle)
      );

      if (!matched.length) matched = [...this.slides];

      // Show/hide slides + matching dots
      this.slides.forEach((s, i) => {
        const visible = matched.includes(s);
        s.style.display    = visible ? '' : 'none';
        s.style.flexShrink = visible ? '0' : '';
        if (this.dots[i]) {
          this.dots[i].style.display = visible ? '' : 'none';
          this.dots[i].classList.remove('is-active');
        }
      });

      // Scroll to first match and activate its dot
      if (matched.length) {
        const first    = matched[0];
        const firstIdx = this.slides.indexOf(first);
        // instant jump so the user sees the right image immediately
        first.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
        if (this.dots[firstIdx]) this.dots[firstIdx].classList.add('is-active');
      }
    }

    _bindDots() {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this._scrollToSlide(this.slides[i]));
      });
    }

    _observe() {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            const idx = parseInt(e.target.dataset.index, 10);
            this.dots.forEach((d, i) => {
              if (d.style.display !== 'none') d.classList.toggle('is-active', i === idx);
            });
          }
        });
      }, { root: this.track, threshold: 0.5 });
      this.slides.forEach(s => obs.observe(s));
    }

    _scrollToSlide(slide) {
      if (!slide || slide.style.display === 'none') return;
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    goTo(idx) { this._scrollToSlide(this.slides[idx]); }

    goToImage(imageId) {
      const slide = this.slides.find(s => s.dataset.imageId === String(imageId));
      if (slide) this._scrollToSlide(slide);
    }
  }

  /* =========================================================================
     Variant picker — swatches + size buttons
     Color swatch click → filterByColor on both galleries
     Other option click → setActive(featuredImage) on both galleries
     ========================================================================= */

  class VariantPicker {
    constructor(section, productData) {
      this.section  = section;
      this.variants = productData.variants;
      this.gallery  = null;
      this.carousel = null;

      this.selectedOptions = this._readSelected();
      this.current = this._find(this.selectedOptions);

      this._bindSwatches();
      this._bindSizes();
    }

    _readSelected() {
      const opts = [];
      qsa('[data-mp-option-pos]', this.section).forEach(group => {
        const active = qs('button.is-selected', group);
        if (active) opts[parseInt(group.dataset.mpOptionPos, 10) - 1] = active.dataset.value;
      });
      return opts;
    }

    _find(options) {
      return this.variants.find(v =>
        v.options.every((opt, i) => !options[i] || opt === options[i])
      ) || this.variants[0];
    }

    _bindSwatches() {
      const labelEl = qs('[data-color-label]', this.section);

      qsa('.mp-swatch', this.section).forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-soldout')) return;
          const group = btn.closest('[data-mp-option-pos]');
          const pos   = parseInt(group.dataset.mpOptionPos, 10) - 1;
          // always treat swatch clicks as color
          this._select(pos, btn.dataset.value, btn, true);
        });

        btn.addEventListener('mouseenter', () => {
          if (labelEl) labelEl.textContent = btn.dataset.value;
        });
        btn.addEventListener('mouseleave', () => {
          if (!labelEl) return;
          const sel = qs('.mp-swatch.is-selected', this.section);
          labelEl.textContent = sel ? sel.dataset.value : '';
        });
      });
    }

    _bindSizes() {
      qsa('.mp-size-btn', this.section).forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-soldout')) return;
          const group   = btn.closest('[data-mp-option-pos]');
          const pos     = parseInt(group.dataset.mpOptionPos, 10) - 1;
          // detect color group by data-is-color attribute (set in Liquid)
          const isColor = group.dataset.isColor === 'true';
          this._select(pos, btn.dataset.value, btn, isColor);
        });
      });
    }

    _select(posIndex, value, btn, isColor) {
      const group = btn.closest('[data-mp-option-pos]');
      qsa('button', group).forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      if (isColor) {
        const labelEl = qs('[data-color-label]', this.section);
        if (labelEl) labelEl.textContent = value;
        console.log('[AYAN] filterByColor →', value); // debug — à retirer après vérification
      }

      this.selectedOptions[posIndex] = value;
      const variant = this._find(this.selectedOptions);
      if (!variant) return;
      this.current = variant;

      this._updatePrice(variant);
      this._updateButton(variant);
      this._updateURL(variant);
      this._updateInput(variant);

      if (isColor) {
        // Filter galleries to show only images for this color
        if (this.gallery)  this.gallery.filterByColor(value);
        if (this.carousel) this.carousel.filterByColor(value);
      } else {
        // Size/other option: jump to featured image if variant has one
        this._updateGallery(variant);
      }
    }

    _updatePrice(variant) {
      const el = qs('[data-mp-price]', this.section);
      if (!el) return;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        el.innerHTML =
          `<span class="mp-price__compare">${formatMoney(variant.compare_at_price)}</span>` +
          `<span class="mp-price__sale">${formatMoney(variant.price)}</span>`;
      } else {
        el.innerHTML = formatMoney(variant.price);
      }
    }

    _updateButton(variant) {
      const btn    = qs('[data-mp-atc]', this.section);
      const textEl = btn && qs('.mp-btn-atc__text', btn);
      if (!btn) return;
      btn.disabled = !variant.available;
      if (textEl) textEl.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }

    _updateURL(variant) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      } catch (_) {}
    }

    _updateInput(variant) {
      const input = qs('[data-mp-variant-id]', this.section);
      if (input) input.value = variant.id;
    }

    _updateGallery(variant) {
      if (!variant.featured_image) return;
      const id = variant.featured_image.id;
      if (this.gallery)  this.gallery.setActive(id);
      if (this.carousel) this.carousel.goToImage(id);
    }
  }

  /* =========================================================================
     Horizontal tabs
     ========================================================================= */

  function initTabs(section) {
    const btns   = qsa('[data-mp-tab-btn]', section);
    const panels = qsa('[data-mp-tab-panel]', section);
    if (!btns.length) return;

    btns.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        btns.forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        panels.forEach(p => p.classList.remove('is-active'));
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        if (panels[i]) panels[i].classList.add('is-active');
      });
    });
  }

  /* =========================================================================
     Product form — ATC ajax + spinner
     ========================================================================= */

  class ProductForm {
    constructor(section) {
      this.section = section;
      this.form    = qs('.mp-form', section);
      this.btn     = qs('[data-mp-atc]', section);
      this.errorEl = qs('.mp-error', section);

      if (!this.form || !this.btn) return;
      this.form.setAttribute('data-mp-atc-handled', 'true');

      // Empêche tout submit natif : click sur le bouton + form.submit() programmatique
      this.btn.setAttribute('type', 'button');
      this.form.submit = function () {};
      this.form.addEventListener('submit', e => { e.preventDefault(); e.stopPropagation(); }, true);

      this.btn.addEventListener('click', e => this._submit(e));

      // Point d'entrée direct pour nos propres déclencheurs internes (ex. la barre
      // collante) : contourne totalement le test e.isTrusted ci-dessous, puisqu'il
      // ne s'agit pas d'un événement "click" simulé mais d'un appel de fonction.
      this.btn.mpTriggerAdd = () => this._submit({ preventDefault: () => {}, isTrusted: true });
    }

    async _submit(e) {
      e.preventDefault();
      if (this.btn.disabled) return;

      this._setLoading(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(this.form)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || 'Unable to add to cart.');
        }
        this._success();
        if (typeof window.__openDrawerRefreshed === 'function') {
          window.__openDrawerRefreshed();
        }
      } catch (err) {
        this._error(err.message);
      } finally {
        setTimeout(() => this._setLoading(false), 500);
      }
    }

    _setLoading(on) {
      const textEl = qs('.mp-btn-atc__text', this.btn);
      this.btn.classList.toggle('is-loading', on);
      if (textEl && !on) textEl.textContent = 'Add to cart';
    }

    _success() {
      const textEl = qs('.mp-btn-atc__text', this.btn);
      if (textEl) textEl.textContent = 'Added!';
      setTimeout(() => { if (textEl) textEl.textContent = 'Add to cart'; }, 2000);
    }

    _error(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.classList.add('is-visible');
      setTimeout(() => this.errorEl.classList.remove('is-visible'), 5000);
    }
  }

  /* =========================================================================
     Boot
     ========================================================================= */

  function init() {
    const section = qs('.mp-section');
    if (!section) return;

    const dataEl = qs('[data-mp-product-json]');
    if (!dataEl) return;
    const productData = JSON.parse(dataEl.textContent);

    // Galleries
    const galleryEl  = qs('[data-mp-gallery]');
    const carouselEl = qs('.mp-carousel-wrap');
    const desktop    = galleryEl  ? new DesktopGallery(galleryEl)  : null;
    const mobile     = carouselEl ? new MobileCarousel(carouselEl) : null;

    // Variants
    const picker    = new VariantPicker(section, productData);
    picker.gallery  = desktop;
    picker.carousel = mobile;

    // Filter gallery on page load to match the initially selected color
    const initialSwatch = qs('.mp-swatch.is-selected', section);
    if (initialSwatch) {
      if (desktop) desktop.filterByColor(initialSwatch.dataset.value);
      if (mobile)  mobile.filterByColor(initialSwatch.dataset.value);
    }

    // Tabs + form
    initTabs(section);
    new ProductForm(section);

    // Guide des tailles — drawer
    const sizeGuide        = document.getElementById('mp-size-guide');
    const sizeGuideBtn     = qs('[data-mp-size-guide-trigger]');
    const sizeGuideClose   = document.getElementById('mp-size-guide-close');
    const sizeGuideOverlay = document.getElementById('mp-size-guide__overlay');

    if (sizeGuide && sizeGuideBtn) {
      function openSizeGuide() {
        sizeGuide.classList.add('is-open');
        sizeGuide.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        const inner = sizeGuide.querySelector('.mp-size-guide__inner');
        if (inner) inner.focus();
      }

      function closeSizeGuide() {
        sizeGuide.classList.remove('is-open');
        sizeGuide.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        sizeGuideBtn.focus();
      }

      sizeGuideBtn.addEventListener('click', openSizeGuide);
      if (sizeGuideClose)   sizeGuideClose.addEventListener('click', closeSizeGuide);
      if (sizeGuideOverlay) sizeGuideOverlay.addEventListener('click', closeSizeGuide);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && sizeGuide.classList.contains('is-open')) closeSizeGuide();
      });

      // Switch CM / INCHES
      qsa('.mp-sg-tab', sizeGuide).forEach(function (btn) {
        btn.addEventListener('click', function () {
          const switcher = btn.closest('.mp-sg-switcher');
          if (!switcher) return;
          qsa('.mp-sg-tab', switcher).forEach(function (b) { b.classList.remove('is-active'); });
          btn.classList.add('is-active');
          switcher.dataset.active = btn.dataset.unit;
        });
      });
    }

    // Force color swatches visible — résiste à tout CSS/JS externe (Fast Bundle)
    function forceSwatchesVisible() {
      section.querySelectorAll('.mp-color-section').forEach(el => {
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('opacity', '1', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.removeAttribute('hidden');
      });
      section.querySelectorAll('.mp-swatches').forEach(el => {
        el.style.setProperty('display', 'flex', 'important');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('opacity', '1', 'important');
        el.removeAttribute('hidden');
      });
      section.querySelectorAll('.mp-swatch').forEach(el => {
        el.style.setProperty('display', 'inline-block', 'important');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('width', '22px', 'important');
        el.style.setProperty('height', '22px', 'important');
        if (!el.classList.contains('is-soldout')) {
          el.style.setProperty('opacity', '1', 'important');
        }
        el.removeAttribute('hidden');
      });
    }

    // Exécuter immédiatement, puis après 500 ms / 1.5 s pour couvrir les scripts tiers
    forceSwatchesVisible();
    setTimeout(forceSwatchesVisible, 500);
    setTimeout(forceSwatchesVisible, 1500);

    // MutationObserver : surveille les modifications de style/attributs sur les swatches
    const mo = new MutationObserver(() => forceSwatchesVisible());
    const colorSection = section.querySelector('.mp-color-section');
    if (colorSection) {
      mo.observe(colorSection.parentElement || section, {
        attributes: true,
        subtree: true,
        attributeFilter: ['style', 'hidden', 'class']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Intercepteur de fallback : bloque tout rechargement de page sur un formulaire
     dont l'action est /cart/add (Fast Bundle, apps tierces, bouton natif non géré).
     Si ProductForm a déjà marqué le formulaire (data-mp-atc-handled), on laisse
     passer — c'est _submit qui gère. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form) return;
    var action = (form.getAttribute('action') || '').replace(/^https?:\/\/[^/]+/, '').split('?')[0];
    if (!/^\/cart\/add/.test(action)) return;
    if (form.hasAttribute('data-mp-atc-handled')) return;
    e.preventDefault();
    e.stopPropagation();
    fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || data.status) return;
        if (typeof window.__openDrawerRefreshed === 'function') {
          window.__openDrawerRefreshed();
        }
      })
      .catch(function () {});
  }, true);

})();
